/* ── Per-user venue manager data store (in-memory only) ──────
   All data is keyed by userId so each account is fully isolated.
   NOTE: This is NOT persisted. Data resets on page reload.
   A proper backend API should replace this for production.
   ─────────────────────────────────────────────────────────────── */

export type EventType = "booking" | "event" | "blocked" | "maintenance";
export type EventStatus = "confirmed" | "pending" | "cancelled";

export interface ManagerEvent {
  id: string;
  title: string;
  date: string;       // YYYY-MM-DD  (start date)
  endDate?: string;   // YYYY-MM-DD  (optional end, for multi-day)
  time: string;       // "HH:MM" 24h
  type: EventType;
  guest?: string;     // for bookings
  notes?: string;
  amount?: number;    // revenue for this event/booking (₦)
  nights?: number;    // for booking type
  status: EventStatus;
  createdAt: string;  // ISO string
}

/* ── in-memory stores ─────────────────────────────────── */
const eventStore = new Map<string, ManagerEvent[]>();
const venueStore = new Map<string, ManagerVenueInfo>();

/* ── events CRUD ──────────────────────────────────────── */
export function getEvents(userId: string): ManagerEvent[] {
  return eventStore.get(userId) ?? [];
}
function saveEvents(userId: string, evs: ManagerEvent[]): void {
  eventStore.set(userId, evs);
}
export function addEvent(userId: string, ev: ManagerEvent): void {
  saveEvents(userId, [...getEvents(userId), ev]);
}
export function removeEvent(userId: string, id: string): void {
  saveEvents(userId, getEvents(userId).filter(e => e.id !== id));
}
export function updateEventStatus(userId: string, id: string, status: EventStatus): void {
  saveEvents(userId, getEvents(userId).map(e => e.id === id ? { ...e, status } : e));
}

/* ── venue info ───────────────────────────────────────── */
export interface ManagerVenueInfo {
  name: string;
  location: string;
  type: string;
  image?: string;
}
export function getVenueInfo(userId: string): ManagerVenueInfo | null {
  return venueStore.get(userId) ?? null;
}
export function saveVenueInfo(userId: string, v: ManagerVenueInfo): void {
  venueStore.set(userId, v);
}

/* ── derived stats ────────────────────────────────────── */
export function computeStats(userId: string) {
  const evs = getEvents(userId);
  const bookings = evs.filter(e => e.type === "booking");
  const confirmed = bookings.filter(b => b.status === "confirmed");
  const pending   = bookings.filter(b => b.status === "pending");
  const totalRevenue  = confirmed.reduce((s, b) => s + (b.amount ?? 0), 0);
  const upcoming = confirmed.filter(b => b.date >= today()).length;
  return { evs, bookings, confirmed, pending, totalRevenue, upcoming };
}

function today() {
  return new Date().toISOString().slice(0, 10);
}
